/**
 * Copyright (c) 2025 Robert Schiwon
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import CustomEventsMessageBus from 'util/CustomEventsMessageBus';
import TreeNode from './TreeNode';
import './Tree.css';
import { isSameUrl } from 'util/UrlUtils';
const template = '';
export default class Tree extends HTMLElement {
    #initialized = false;
    #channel = CustomEventsMessageBus.getDefaultChannel();
    #eventListeners = [];
    #children = [];
    #selectedNodeUrl = null;
    init() {
        if (!this.#initialized) {
            this.innerHTML = template;
            this.#initialized = true;
        }
    }
    render() {
        const rootNodes = this.#children;
        const children = this.children;
        let i = 0;
        // TODO: do more generic set reconciliation
        // assumes that new root nodes are always appended, not inserted
        for (const rootNode of rootNodes) {
            if (i >= children.length) {
                this.append(rootNode);
                i += 1;
            }
            else {
                const rootNodeUrl = rootNode.getAttribute('url');
                while (i < children.length) {
                    const childNode = children.item(i);
                    const childNodeUrl = childNode.getAttribute('url');
                    if (rootNodeUrl === childNodeUrl) {
                        // matches => noop
                        i += 1;
                        break;
                    }
                    else {
                        // surplus child node => remove
                        this.removeChild(children.item(i));
                        // children gets updated => do not i++
                    }
                }
            }
        }
        // more children than root nodes => remove extra children
        while (i < children.length) {
            this.removeChild(children.item(i));
        }
    }
    addRootNode(parser) {
        const rootNode = new TreeNode(parser, parser.rootUrl);
        this.#children.push(rootNode);
        this.render();
    }
    // #region user events
    removeSelectedNode() {
        if (!this.#selectedNodeUrl) {
            return null;
        }
        const selectedUrl = this.#selectedNodeUrl.toString();
        for (const child of this.#children) {
            const childUrl = child.getAttribute('url');
            if (childUrl !== null && selectedUrl.startsWith(childUrl)) {
                const index = this.#children.indexOf(child);
                this.#children.splice(index, 1);
                // todo: return Parser and close in App?
                this.#selectedNodeUrl = null;
                child.close();
                // child.close().then(() => {
                //   console.log(`File closed: ${child.name}`);
                // });
                this.render();
                return new URL(childUrl);
            }
        }
        throw new Error(`Illegal state. No node found for selectedNodeUrl: ${this.#selectedNodeUrl}`);
    }
    removeAllNodes() {
        const urls = [];
        for (const child of this.#children) {
            const childUrl = child.getAttribute('url');
            urls.push(new URL(childUrl));
            child.close();
            // child.close().then(() => {
            //   console.log(`File closed: ${child.name}`);
            // });
        }
        this.#selectedNodeUrl = null;
        this.#children = [];
        this.render();
        return urls;
    }
    #handleTreeNodeSelection(message) {
        const url = message.detail.url;
        if (message.name === 'sf-tree-node-selected') {
            this.#selectedNodeUrl = url;
        }
        else if (message.name === 'sf-tree-node-deselected') {
            if (this.#selectedNodeUrl === url) {
                this.#selectedNodeUrl = null;
            }
        }
    }
    static #findLastLeafNode(node) {
        // recursively find last TreeNode
        let currentNode = node;
        while (currentNode.hasAttribute('expand') &&
            currentNode.getAttribute('expand') === 'true') {
            currentNode = currentNode.lastChild;
        }
        return currentNode;
    }
    static #findPreviousTreeNode(element) {
        let prev = element.previousSibling;
        if (prev === null || !(prev instanceof TreeNode)) {
            prev = element.parentElement;
            if (!(prev instanceof TreeNode)) {
                prev = null;
            }
        }
        else if (prev !== null &&
            prev.hasAttribute('expand') &&
            prev.getAttribute('expand') === 'true') {
            prev = Tree.#findLastLeafNode(prev);
        }
        return prev;
    }
    static #findParentNextSibling(node) {
        // recursively move up tree to find parent's next sibling
        let parent = node.parentElement;
        while (parent instanceof TreeNode || parent instanceof Tree) {
            const nextSibling = parent.nextSibling;
            if (nextSibling instanceof TreeNode) {
                return nextSibling;
            }
            parent = parent.parentElement;
        }
        return null;
    }
    static #findNextTreeNode(element) {
        let next = null;
        if (element.hasAttribute('expand') &&
            element.getAttribute('expand') === 'true') {
            // find first child node
            next = element.querySelector('sf-tree-node');
        }
        else {
            // find next sibling
            next = element.nextSibling;
            if (next === null || !(next instanceof TreeNode)) {
                next = Tree.#findParentNextSibling(element);
            }
        }
        return next;
    }
    #findSelectedNode() {
        const node = this.querySelector(`sf-tree-node[url="${this.#selectedNodeUrl}"]`);
        return node === null ? null : node;
    }
    getSelectedNodeParser() {
        const node = this.#findSelectedNode();
        if (node instanceof TreeNode) {
            return node.parser;
        }
        return null;
    }
    static #findEventNode(e) {
        let node = e.target;
        while (node !== null && node !== undefined && !(node instanceof TreeNode)) {
            node = node?.parentElement;
        }
        return node == null || node == undefined ? null : node;
    }
    onKeyDown(e) {
        const key = e.key;
        if (key === 'Enter') {
            // event may originate from element within TreeNode => find TreeNode parentElement
            const treeNode = Tree.#findEventNode(e);
            if (treeNode === null) {
                return;
            }
            let isSelectedNode = false;
            if (isSameUrl(treeNode.getAttribute('url'), this.#selectedNodeUrl)) {
                isSelectedNode = true;
            }
            // only toggle expanded state if already selected
            if (isSelectedNode && treeNode.hasAttribute('expand')) {
                const expanded = treeNode.getAttribute('expand') === 'true';
                treeNode.setExpand(!expanded);
            }
            treeNode.setSelected(true);
            return;
        }
        const selectedNode = this.#findSelectedNode();
        if (selectedNode === null) {
            return;
        }
        switch (key) {
            case 'ArrowUp': {
                const prev = Tree.#findPreviousTreeNode(selectedNode);
                prev?.setSelected(true);
                // don't scroll
                e.preventDefault();
                break;
            }
            case 'ArrowDown': {
                const next = Tree.#findNextTreeNode(selectedNode);
                next?.setSelected(true);
                // don't scroll
                e.preventDefault();
                break;
            }
            case 'ArrowLeft':
                selectedNode.setExpand(false);
                selectedNode.setSelected(true);
                // do not scroll view
                e.preventDefault();
                break;
            case 'ArrowRight':
                selectedNode.setExpand(true);
                selectedNode.setSelected(true);
                // do not scroll view
                e.preventDefault();
                break;
            default:
                break;
        }
    }
    onClick = (e) => {
        if (e.target instanceof TreeNode || e.target instanceof Tree) {
            // TODO: refactor to avoid code duplication and knowledge of TreeNode implementation
            const nameElement = this.querySelector(`span[url="${this.#selectedNodeUrl}"]`);
            nameElement?.focus({ focusVisible: false });
        }
    };
    // #endregion user events
    // #region lifecycle events
    connectedCallback() {
        this.init();
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('click', this.onClick);
        const selectedHandle = this.#channel.addListener('sf-tree-node-selected', this.#handleTreeNodeSelection.bind(this));
        const deselectedHandle = this.#channel.addListener('sf-tree-node-deselected', this.#handleTreeNodeSelection.bind(this));
        this.#eventListeners.push(selectedHandle);
        this.#eventListeners.push(deselectedHandle);
        this.render();
    }
    disconnectedCallback() {
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('click', this.onClick);
        for (const handle of this.#eventListeners) {
            this.#channel.removeListener(handle);
        }
    }
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    attributeChangedCallback(name, oldValue, newValue) {
        this.init();
    }
}
customElements.define('sf-tree', Tree);
